import React from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import * as yup from "yup";
import { Button, CircularProgress, TextField } from "@mui/material";
import { ValidationError } from "components";
import { useFormik } from "formik";
import apiErrorsToFormik from "utils/api-errors-to-formik";

import { Routes } from "../../constants/routes";
import { useActionHook, useDidUpdate } from "../../hooks";
import { useAuthActions } from "../../store";

const passInitValues = {
  password: "",
  confirmation: ""
};

const passValidation = yup.object().shape({
  password: yup.string().required("Password is required").min(8),
  confirmation: yup
    .string()
    .required("Confirmation is required")
    .oneOf([yup.ref("password"), null], "password doesn't match")
});

interface IPasswordForm {
  email: string;
  token: string;
}

export const PasswordForm: React.FC<IPasswordForm> = ({ email, token }) => {
  const { resetPasswordAction } = useAuthActions();
  const { push } = useHistory();

  const {
    error: apiErrors,
    loading,
    action: resetPass
  } = useActionHook(resetPasswordAction, {
    onSuccess: () => {
      toast("The password has been reset successfully");
      push(Routes.login);
    }
  });

  const { handleSubmit, values, errors, isValid, setErrors, handleChange } = useFormik({
    initialValues: passInitValues,
    validationSchema: passValidation,
    onSubmit: async (values) =>
      resetPass({
        password: values.password,
        password_confirmation: values.confirmation,
        email,
        token
      })
  });

  useDidUpdate(() => {
    if (apiErrors?.errors) {
      setErrors(apiErrorsToFormik(passInitValues, apiErrors.errors));
    }
  }, [apiErrors]);

  return (
    <form className="reset-pass-form" onSubmit={handleSubmit}>
      <ValidationError messages={apiErrors?.errors?.token || []} />
      <ValidationError messages={apiErrors?.errors?.email || []} />
      <TextField
        label="Enter a New Password"
        placeholder="New password"
        name="password"
        sx={{ width: 300 }}
        value={values.password}
        error={!!errors.password}
        helperText={errors.password}
        type="password"
        onChange={handleChange}
      />
      <TextField
        label="Confirm a New Password"
        placeholder="Confirm new password"
        name="confirmation"
        sx={{ width: 300 }}
        value={values.confirmation}
        type="password"
        error={!!errors.confirmation}
        helperText={errors.confirmation}
        onChange={handleChange}
      />
      <Button
        variant="contained"
        sx={{
          borderRadius: "5px",
          textTransform: "none",
          width: "100%",
          height: 45,
          color: "#26A6AE"
        }}
        type="submit"
        disabled={!isValid || loading}
      >
        {loading ? <CircularProgress sx={{ color: "#5d7782" }} /> : "Reset Password"}
      </Button>
    </form>
  );
};
